import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSeatTypes } from './useSeatTypes';
import configService from '../../shared/services/configService';
import { theaterReservationService } from '../../shared/services';

export const useSeats = (maxTicketsPerCode = 6, eventId = null, reservationsToCancel = [], viewMode = false, existingReservations = [], reservationsToReturn = [], isOpenSale = false, isFreeTicket = false, isAdmin = false) => {
  
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [palcoCapacity, setPalcoCapacity] = useState(1); // Capacidad por defecto de palcos
  const [occupiedSeats, setOccupiedSeats] = useState([]);
  const [occupiedSeatsWithStatus, setOccupiedSeatsWithStatus] = useState([]);
  const [loadingOccupiedSeats, setLoadingOccupiedSeats] = useState(false);

  const { seatTypes, loading: seatTypesLoading, error: seatTypesError, createDefaults, refetch: refetchSeatTypes } = useSeatTypes(eventId);

  // Cargar configuración de capacidad de palcos
  useEffect(() => {
    const loadPalcoCapacity = async () => {
      try {
        const config = await configService.getConfigByKey('seats_per_box');
        if (config && config.value) {
          const capacity = parseInt(config.value);
          if (!isNaN(capacity) && capacity > 0) {
            setPalcoCapacity(capacity);
          }
        }
      } catch (error) {
        console.log('No se encontró configuración de capacidad de palcos, usando valor por defecto:', palcoCapacity);
      }
    };

    loadPalcoCapacity();
  }, [palcoCapacity]);

  // Cargar asientos ocupados desde el backend
  const loadOccupiedSeats = useCallback(async () => {
    if (!eventId) return;
    
    setLoadingOccupiedSeats(true);
    try {
      // Para vista de administrador, cargar con estados
      if (viewMode) {
        const occupiedSeatsWithStatusData = await theaterReservationService.getOccupiedSeatsWithStatus(eventId);
        setOccupiedSeatsWithStatus(occupiedSeatsWithStatusData);
        setOccupiedSeats(occupiedSeatsWithStatusData.map(seat => seat.compositeId));
      } else {
        // Para vista de usuario regular, usar endpoint básico
        const occupiedSeatsData = await theaterReservationService.getOccupiedSeats(eventId);
        setOccupiedSeats(occupiedSeatsData);
        setOccupiedSeatsWithStatus([]);
      }
    } catch (error) {
      console.error('Error al cargar asientos ocupados:', error);
      setOccupiedSeats([]);
      setOccupiedSeatsWithStatus([]);
    } finally {
      setLoadingOccupiedSeats(false);
    }
  }, [eventId, viewMode]);

  // Cargar asientos ocupados cuando cambie el eventId o isOpenSale
  useEffect(() => {
    loadOccupiedSeats();
  }, [loadOccupiedSeats, isOpenSale]);

  const seatTypePriceMap = useMemo(() => {
    const map = {};
    seatTypes.forEach(st => {
      map[st.type] = st.price;
    });
    return map;
  }, [seatTypes]);

  const seatTypeList = useMemo(() => {
    const mapped = seatTypes.map(st => ({ 
      type: st.type, 
      price: st.price,
      description: st.description || st.type, // Usar descripción o fallback al tipo
      category: st.category,
      priority: st.priority,
      color: st.color
    }));
    return mapped;
  }, [seatTypes]);

  // Regenerar asientos cuando cambien los tipos de asientos o los asientos ocupados
  const allSeats = useMemo(() => {
    // Si no hay tipos y no estamos en modo visualización, no generamos asientos
    if (seatTypes.length === 0 && !viewMode) return [];

    const seats = [];

    let sectionTypes = seatTypes.filter(st => st.category === 'section');
    let rowRangeTypes = seatTypes.filter(st => st.category === 'row_range');
    let boxTypes = seatTypes.filter(st => st.category === 'box');

    if (seatTypes.length === 0 && viewMode) {
      sectionTypes = [
        { type: 'planta-baja', price: 50, section: 'planta_baja' },
        { type: 'primer-piso', price: 75, section: 'primer_piso' }
      ];
      boxTypes = [{ type: 'palco', price: 100 }];
    }

    const plantaBajaType = sectionTypes.find(st => st.section === 'planta_baja') || sectionTypes[0];
    const primerPisoType = sectionTypes.find(st => st.section === 'primer_piso') || sectionTypes[0];
    const palcoType = boxTypes[0] || sectionTypes[0];

    const plantaBajaColumns = [
      { number: 29, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 27, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 25, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 23, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 21, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 19, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 17, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 15, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: null, type: 'pasillo', price: 0 },
      { number: 13, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 11, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 9, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 7, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 5, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 3, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 1, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 2, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 4, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 6, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 8, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 10, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 12, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 14, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: null, type: 'pasillo', price: 0 },
      { number: 16, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 18, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 20, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 22, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 24, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 26, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 28, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
      { number: 30, type: plantaBajaType?.type, price: seatTypePriceMap[plantaBajaType?.type] || 0 },
    ];

    const primerPisoColumns = Array.from({ length: 26 }, (_, idx) => {
      return {
        number: 26 - idx,
        type: primerPisoType?.type,
        price: seatTypePriceMap[primerPisoType?.type] || 0,
      };
    });

    // Función para determinar el estado del asiento basado en las reservas
    const getSeatStudentName = (compositeId) => {
      if (!viewMode) return null;
      const seatReservation = occupiedSeatsWithStatus.find(seat => seat.compositeId === compositeId);
      return seatReservation?.studentName || null;
    };

    const getSeatStatus = (compositeId) => {
      if (viewMode && occupiedSeatsWithStatus.length > 0) {
        const seatReservation = occupiedSeatsWithStatus.find(seat => seat.compositeId === compositeId);
        if (seatReservation) {
          // SOLD -> "sold" (vendido - verde)
          // BLOCKED -> "blocked" (bloqueado - ámbar)
          // PENDING/CONFIRMED -> "reserved" (reservado - azul claro)
          switch (seatReservation.status) {
            case 'sold':
            case 'delivered':
            case 'completed':
              return "sold";
            case 'blocked':
              return "blocked";
            case 'pending':
            case 'confirmed':
              return "reserved";
            default:
              return "booked";
          }
        }
        return "available";
      } else {
        // Vista de usuario normal - usar lógica existente
        return occupiedSeats.includes(compositeId) ? "booked" : "available";
      }
    };

    const getSeatTypeAndPrice = (section, row, seatNumber) => {
      
      for (const rowType of rowRangeTypes) {
        const sectionMatches = rowType.section === section || 
                              rowType.section === section.replace('-', '_') ||
                              (rowType.section === '' && section === 'planta-baja');
        
        if (sectionMatches && 
            row >= rowType.startRow && 
            row <= rowType.endRow) {
          return { type: rowType.type, price: rowType.price };
        }
      }
      
      if (section === 'planta-baja') {
        return { type: plantaBajaType?.type || 'planta_baja', price: seatTypePriceMap[plantaBajaType?.type] || 0 };
      } else if (section === 'primer-piso') {
        return { type: primerPisoType?.type || 'primer_piso', price: seatTypePriceMap[primerPisoType?.type] || 0 };
      }
      
      return { type: 'unknown', price: 0 };
    };

    const generatePrimerPisoSeats = () => {
      const primerPisoSeats = [];
      for (let row = 1; row <= 17; row++) {
        primerPisoColumns.forEach((column, columnIndex) => {
          const compositeId = `PP-${row}-${column.number}`;
          const { type, price } = getSeatTypeAndPrice('primer-piso', row, column.number);
          
          primerPisoSeats.push({
            id: compositeId,
            row: row,
            column: columnIndex,
            number: column.number,
            status: getSeatStatus(compositeId),
            studentName: getSeatStudentName(compositeId),
            type: type,
            price: price,
            section: "primer-piso",
          });
        });
      }
      return primerPisoSeats;
    };

    const rowAvailability = {
      1: [false, false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, false, false, false],
      2: [false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, false, false],
      3: [false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      4: [false, false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      5: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      6: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      7: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      8: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      9: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      10: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      11: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      12: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      13: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      14: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      15: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      16: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      17: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      18: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      19: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      20: [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      21: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      22: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      23: [false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
      24: [false, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
    };

    // Generar asientos de Planta Baja
    Object.entries(rowAvailability).forEach(([rowNum, availability]) => {
      availability.forEach((isAvailable, columnIndex) => {
        const column = plantaBajaColumns[columnIndex];
        
        // Verificar que la columna existe antes de acceder a sus propiedades
        if (column && isAvailable && column.type !== "pasillo") {
          const compositeId = `PB-${rowNum}-${column.number}`;
          const { type, price } = getSeatTypeAndPrice('planta-baja', parseInt(rowNum), column.number);
          
          seats.push({
            id: compositeId,
            row: parseInt(rowNum),
            column: columnIndex,
            number: column.number,
            status: getSeatStatus(compositeId),
            studentName: getSeatStudentName(compositeId),
            type: type,
            price: price,
            section: "planta-baja",
          });
        }
      });
    });

    // Generar asientos del Primer Piso (usando la función específica)
    const primerPisoSeats = generatePrimerPisoSeats();
    seats.push(...primerPisoSeats);

    // Generar asientos de palcos (boxes) con numeración correcta según el patrón especificado
    const boxRows = [1, 3, 5, 7, 9, 11, 13, 15];
    const palcoTypeName = palcoType?.type || seatTypeList[0]?.type;
    const palcoPrice = seatTypePriceMap[palcoTypeName] || 0;
    
    let palcoNumber = 1;
    
    // Primera secuencia: IZQ2-DER1 alternando por filas (1-12)
    boxRows.forEach((rowNumber) => {
      if (rowNumber <= 11) {
        // Palco izquierdo columna 2
        const compositeIdL2 = `BOX-${rowNumber}-L2`;
        seats.push({
          id: compositeIdL2,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdL2),
          studentName: getSeatStudentName(compositeIdL2),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-izq',
          visible: true,
        });
        
        // Palco derecho columna 1
        const compositeIdR1 = `BOX-${rowNumber}-R1`;
        seats.push({
          id: compositeIdR1,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdR1),
          studentName: getSeatStudentName(compositeIdR1),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-der',
          visible: true,
        });
      }
    });
    
    // Segunda secuencia: IZQ1-DER2 alternando por filas (13-24)
    boxRows.forEach((rowNumber) => {
      if (rowNumber <= 11) {
        // Palco izquierdo columna 1
        const compositeIdL1 = `BOX-${rowNumber}-L1`;
        seats.push({
          id: compositeIdL1,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdL1),
          studentName: getSeatStudentName(compositeIdL1),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-izq',
          visible: rowNumber === 1 ? false : true,
        });
        // Palco derecho columna 2
        const compositeIdR2 = `BOX-${rowNumber}-R2`;
        seats.push({
          id: compositeIdR2,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdR2),
          studentName: getSeatStudentName(compositeIdR2),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-der',
          visible: rowNumber === 1 ? false : true,
        });
      }
    });
    
    // Tercera secuencia: IZQ1-DER2 para filas 13,15 (25-28)
    boxRows.forEach((rowNumber) => {
      if (rowNumber >= 13) {
        const compositeIdL1 = `BOX-${rowNumber}-L1`;
        seats.push({
          id: compositeIdL1,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdL1),
          studentName: getSeatStudentName(compositeIdL1),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-izq',
          visible: true,
        });
        
        const compositeIdR2 = `BOX-${rowNumber}-R2`;
        seats.push({
          id: compositeIdR2,
          row: rowNumber,
          column: 0,
          number: palcoNumber++,
          status: getSeatStatus(compositeIdR2),
          studentName: getSeatStudentName(compositeIdR2),
          type: palcoTypeName,
          price: palcoPrice,
          section: 'palco-der',
          visible: true,
        });
      }
    });

    return seats;
  }, [seatTypes, seatTypeList, seatTypePriceMap, occupiedSeats, viewMode, occupiedSeatsWithStatus]);

  // Calcular el conteo efectivo de asientos seleccionados (considerando capacidad de palcos y reservas existentes)
  const effectiveSeatCount = useMemo(() => {
    // Si es admin, la venta está abierta o es entrada gratuita, no hay límites
    if (isAdmin || isOpenSale || isFreeTicket) {
      return 0; // Siempre se puede seleccionar más
    }
    
    let count = 0;
    
    // Contar todas las reservas que cuentan para el límite, excluyendo entradas gratuitas
    // Esto incluye tanto reservas existentes como asientos seleccionados actualmente
    existingReservations.forEach(reservation => {
      const isMarkedForReturn = reservationsToReturn.some(r => r.id === reservation.id);
      const isMarkedForCancellation = reservationsToCancel.some(r => r.id === reservation.id);
      
      // Solo contar reservas que:
      // 1. NO están marcadas para devolución o cancelación
      // 2. Tienen status válido (sold, confirmed, completed, pending, delivered)
      // 3. NO son gratuitas (price > 0)
      if (!isMarkedForReturn && !isMarkedForCancellation && (
        reservation.status === 'sold' || 
        reservation.status === 'confirmed' || 
        reservation.status === 'completed' ||
        reservation.status === 'pending' ||
        reservation.status === 'delivered'
      )) {
        // Verificar que la reserva NO sea gratuita
        if (reservation.price !== undefined && reservation.price !== null && parseFloat(reservation.price) > 0) {
          // Verificar si es un palco basado en el compositeId
          if (reservation.compositeId && reservation.compositeId.startsWith('BOX-')) {
            count += palcoCapacity; // Palco cuenta como múltiples asientos
          } else {
            count += 1; // Asiento normal cuenta como 1
          }
        }
        // Las reservas con price = 0 (gratuitas) no se cuentan en el límite
      }
    });
    
    // Agregar asientos seleccionados que NO están en existingReservations
    // (nuevos asientos que se están seleccionando)
    selectedSeats.forEach(seatId => {
      const isInExistingReservations = existingReservations.some(r => r.compositeId === seatId);
      const isMarkedForCancellation = reservationsToCancel.some(reservation => reservation.compositeId === seatId);
      const isMarkedForReturn = reservationsToReturn.some(reservation => reservation.compositeId === seatId);
      
      // Solo contar asientos que:
      // 1. NO están en reservas existentes (son nuevos)
      // 2. NO están marcados para cancelación o devolución
      if (!isInExistingReservations && !isMarkedForCancellation && !isMarkedForReturn) {
        const seat = allSeats.find(s => s.id === seatId || s.compositeId === seatId);
        // Solo contar asientos que NO son gratuitos (price > 0)
        if (seat && seat.price !== undefined && seat.price !== null && parseFloat(seat.price) > 0) {
          if (seat && (seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco'))) {
            count += palcoCapacity; // Palco cuenta como múltiples asientos
          } else {
            count += 1; // Asiento normal cuenta como 1
          }
        }
        // Los asientos con price = 0 (gratuitos) no se cuentan en el límite
      }
    });
    
    return count;
  }, [selectedSeats, palcoCapacity, allSeats, reservationsToCancel, reservationsToReturn, existingReservations, isOpenSale, isFreeTicket, isAdmin]);

  // Verificar si se puede seleccionar más asientos
  // Si es admin, la venta está abierta o es entrada gratuita, siempre se puede seleccionar más
  const canSelectMore = isAdmin || isOpenSale || isFreeTicket || effectiveSeatCount < maxTicketsPerCode;
  
  // Si isOpenSale cambió, forzar re-render
  useEffect(() => {
    // Este useEffect se ejecuta cuando cambia isOpenSale
  }, [isOpenSale]);
  
  


  const [onSeatDeselectedCallback, setOnSeatDeselectedCallback] = useState(null);
  const [onSeatReselectedCallback, setOnSeatReselectedCallback] = useState(null);

  const setSeatDeselectedCallback = useCallback((callback) => {
    setOnSeatDeselectedCallback(() => callback);
  }, []);

  const setSeatReselectedCallback = useCallback((callback) => {
    setOnSeatReselectedCallback(() => callback);
  }, []);

  // Función para mapear números de asientos a posiciones físicas según la distribución del teatro
  const getSeatPhysicalPosition = useCallback((seatNumber) => {
    // Distribución especial: impares a la izquierda, pares a la derecha, 1 en el centro
    const seatMap = {
      29: 1, 27: 2, 25: 3, 23: 4, 21: 5, 19: 6, 17: 7, 15: 8, 13: 9, 11: 10, 9: 11, 7: 12, 5: 13, 3: 14, 1: 15,
      2: 16, 4: 17, 6: 18, 8: 19, 10: 20, 12: 21, 14: 22, 16: 23, 18: 24, 20: 25, 22: 26, 24: 27, 26: 28, 28: 29, 30: 30
    };
    
    return seatMap[seatNumber] || seatNumber;
  }, []);

  // Función para obtener el número de asiento desde la posición física
  const getSeatNumberFromPosition = useCallback((physicalPosition) => {
    const reverseMap = {
      1: 29, 2: 27, 3: 25, 4: 23, 5: 21, 6: 19, 7: 17, 8: 15, 9: 13, 10: 11, 11: 9, 12: 7, 13: 5, 14: 3, 15: 1,
      16: 2, 17: 4, 18: 6, 19: 8, 20: 10, 21: 12, 22: 14, 23: 16, 24: 18, 25: 20, 26: 22, 27: 24, 28: 26, 29: 28, 30: 30
    };
    
    return reverseMap[physicalPosition] || physicalPosition;
  }, []);

  // Función para validar que no haya asientos individuales vacíos entre selecciones
  const validateSeatSelection = useCallback((newSelectedSeats) => {
    if (newSelectedSeats.length <= 1) return { isValid: true, message: '' };

    // Agrupar asientos seleccionados por sección y fila
    const seatsByRow = {};
    
    newSelectedSeats.forEach(seatId => {
      const seat = allSeats.find(s => s.id === seatId);
      if (seat) {
        const key = `${seat.section}-${seat.row}`;
        if (!seatsByRow[key]) {
          seatsByRow[key] = [];
        }
        const seatData = {
          id: seatId,
          seatNumber: seat.number,
          physicalPosition: getSeatPhysicalPosition(seat.number),
          section: seat.section,
          row: seat.row
        };
        seatsByRow[key].push(seatData);
      }
    });

    // Verificar cada fila por asientos individuales vacíos
    for (const [, seats] of Object.entries(seatsByRow)) {
      if (seats.length < 2) continue; // Solo verificar filas con múltiples asientos

      // Ordenar asientos por posición física (no por número de asiento)
      seats.sort((a, b) => a.physicalPosition - b.physicalPosition);

      // Verificar si hay asientos individuales vacíos entre selecciones
      for (let i = 0; i < seats.length - 1; i++) {
        const currentSeat = seats[i];
        const nextSeat = seats[i + 1];
        
        // Si hay más de una posición física entre dos selecciones, verificar que no sea solo una
        if (nextSeat.physicalPosition - currentSeat.physicalPosition > 1) {
          // Buscar si hay exactamente una posición vacía entre ellos
          const positionsBetween = nextSeat.physicalPosition - currentSeat.physicalPosition - 1;
          
          if (positionsBetween === 1) {
            // Hay exactamente una posición vacía entre dos selecciones
            const emptyPhysicalPosition = currentSeat.physicalPosition + 1;
            const emptySeatNumber = getSeatNumberFromPosition(emptyPhysicalPosition);
            
            // Construir el ID del asiento vacío usando el mismo formato que los asientos existentes
            const sectionPrefix = currentSeat.section === 'planta-baja' ? 'PB' : 
                                 currentSeat.section === 'primer-piso' ? 'PP' : 
                                 currentSeat.section;
            const emptySeatId = `${sectionPrefix}-${currentSeat.row}-${emptySeatNumber}`;
            
            // Verificar que el asiento vacío no esté ocupado por otro usuario
            const emptySeat = allSeats.find(s => s.id === emptySeatId);
            if (emptySeat && emptySeat.status !== 'booked') {
                          return {
              isValid: false,
              message: `No se puede seleccionar este asiento porque dejaría un asiento individual vacío. Selecciona asientos contiguos.`
            };
            }
          }
        }
      }
    }

    return { isValid: true, message: '' };
  }, [allSeats, getSeatPhysicalPosition, getSeatNumberFromPosition]);

  // Función para obtener asientos recomendados cuando hay un asiento individual vacío
  const getRecommendedSeats = useCallback((selectedSeats) => {
    const recommendations = [];
    
    // Agrupar asientos seleccionados por sección y fila
    const seatsByRow = {};
    
    selectedSeats.forEach(seatId => {
      const seat = allSeats.find(s => s.id === seatId);
      if (seat) {
        const key = `${seat.section}-${seat.row}`;
        if (!seatsByRow[key]) {
          seatsByRow[key] = [];
        }
        seatsByRow[key].push({
          id: seatId,
          seatNumber: seat.number,
          physicalPosition: getSeatPhysicalPosition(seat.number),
          section: seat.section,
          row: seat.row
        });
      }
    });

    // Buscar asientos individuales vacíos en cada fila
    for (const [, seats] of Object.entries(seatsByRow)) {
      if (seats.length < 2) continue;

      seats.sort((a, b) => a.physicalPosition - b.physicalPosition);

      for (let i = 0; i < seats.length - 1; i++) {
        const currentSeat = seats[i];
        const nextSeat = seats[i + 1];
        
        if (nextSeat.physicalPosition - currentSeat.physicalPosition === 2) {
          // Hay exactamente una posición vacía entre dos selecciones
          const emptyPhysicalPosition = currentSeat.physicalPosition + 1;
          const emptySeatNumber = getSeatNumberFromPosition(emptyPhysicalPosition);
          
          // Construir el ID del asiento vacío usando el mismo formato que los asientos existentes
          const sectionPrefix = currentSeat.section === 'planta-baja' ? 'PB' : 
                               currentSeat.section === 'primer-piso' ? 'PP' : 
                               currentSeat.section;
          const emptySeatId = `${sectionPrefix}-${currentSeat.row}-${emptySeatNumber}`;
          
          // Verificar que el asiento vacío esté disponible
          const emptySeat = allSeats.find(s => s.id === emptySeatId);
          if (emptySeat && emptySeat.status !== 'booked') {
            recommendations.push({
              seatId: emptySeatId,
              reason: `Asiento individual vacío entre ${currentSeat.id} y ${nextSeat.id}`,
              section: currentSeat.section,
              row: currentSeat.row,
              seatNumber: emptySeatNumber
            });
          }
        }
      }
    }

    return recommendations;
  }, [allSeats, getSeatPhysicalPosition, getSeatNumberFromPosition]);

  const handleSeatClick = (seatId) => {
    const seat = allSeats.find((s) => s.id === seatId);
    if (!seat) return;

    // Si el asiento está seleccionado por el usuario, validar antes de deseleccionarlo
    if (selectedSeats.includes(seatId)) {
      // Crear la lista de asientos que quedarían después de deseleccionar
      const newSelectedSeats = selectedSeats.filter((id) => id !== seatId);
      
      // Validar que no queden asientos individuales vacíos
      const validation = validateSeatSelection(newSelectedSeats);
      
      if (!validation.isValid) {
        // Mostrar alerta y no permitir la deselección
        if (window.showSeatValidationError) {
          window.showSeatValidationError(validation.message);
        }
        return; // No permitir deseleccionar
      }
      
      // Si la validación pasa, permitir deseleccionar
      setSelectedSeats(newSelectedSeats);
      // Siempre ejecutar el callback si existe
      if (onSeatDeselectedCallback) {
        onSeatDeselectedCallback(seatId);
      }
      return;
    }

    // Verificar si el asiento está marcado para cancelación (disponible para reseleccionar)
    const isMarkedForCancellation = reservationsToCancel.some(reservation => reservation.compositeId === seatId);

    // Si no está seleccionado, verificar que no esté ocupado por otro usuario
    // (a menos que esté marcado para cancelación)
    if (seat.status === "booked" && !isMarkedForCancellation) return;

    // Verificar el límite considerando capacidad de palcos
    // Solo aplicar límites si NO es admin, la venta NO está abierta y NO es entrada gratuita
    if (!isAdmin && !isOpenSale && !isFreeTicket) {
      const isPalco = seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco');
      const seatCount = isPalco ? palcoCapacity : 1;
      if (effectiveSeatCount + seatCount > maxTicketsPerCode) {
        return; // No permitir seleccionar más asientos
      }
    }
    
    // Crear la nueva lista de asientos seleccionados
    const newSelectedSeats = [...selectedSeats, seatId];
    
    // Validar que no haya asientos individuales vacíos
    const validation = validateSeatSelection(newSelectedSeats);
    
    if (!validation.isValid) {
      // Opcional: mostrar un toast o alert con el mensaje
      if (window.showSeatValidationError) {
        window.showSeatValidationError(validation.message);
      }
      return; // No permitir la selección
    }
    
    // Agregar el asiento si la validación pasa
    setSelectedSeats(newSelectedSeats);
    
    // Si el asiento estaba marcado para cancelación, ejecutar el callback de reselección
    if (isMarkedForCancellation && onSeatReselectedCallback) {
      onSeatReselectedCallback(seatId);
    }
  };

  // Función para limpiar asientos seleccionados
  const clearSelectedSeats = () => {
    setSelectedSeats([]);
  };

  // Función para cargar asientos existentes (para modificación)
  const loadExistingSeats = (existingSeatIds) => {
    setSelectedSeats(existingSeatIds || []);
  };



  const getSeatClassName = (seat) => {
    const status = selectedSeats.includes(seat.id) ? "selected" : seat.status;
    const cursorClass = viewMode ? "" : "cursor-pointer";
    const baseClasses = `w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 rounded-full border ${cursorClass} transition-colors duration-200`;

    // Verificar si el asiento está marcado para cancelación o devolución
    const isMarkedForCancellation = reservationsToCancel.some(reservation => reservation.compositeId === seat.id);
    const isMarkedForReturn = reservationsToReturn.some(reservation => reservation.compositeId === seat.id);

    // Estados específicos para vista de administrador
    if (viewMode) {
      if (status === "sold") {
        // Asientos vendidos: rgb(22 101 52) - verde oscuro
        return `${baseClasses} border-green-800 cursor-default` + 
               ` style-sold`;
      }
      
      if (status === "reserved") {
        // Asientos reservados: rgb(239 246 255) - azul muy claro
        return `${baseClasses} border-blue-300 cursor-default` + 
               ` style-reserved`;
      }

      if (status === "blocked") {
        // Asientos bloqueados: tono ámbar claro
        return `${baseClasses} border-amber-300 cursor-default` +
               ` style-blocked`;
      }
    }

    if ((status === "booked" || status === "sold" || status === "reserved" || status === "blocked") && !isMarkedForCancellation && !isMarkedForReturn) {
      const cursorClass = viewMode ? "" : "cursor-not-allowed";
      return `${baseClasses} bg-gray-400 border-gray-500 ${cursorClass} opacity-60`;
    }

    if (status === "selected") {
      return `${baseClasses} bg-blue-500 border-blue-700 ring-2 ring-blue-300`;
    }

    // Asientos marcados para devolución (mostrar con estilo especial)
    if (isMarkedForReturn) {
      return `${baseClasses} bg-orange-300 border-orange-500 ring-2 ring-orange-200 cursor-pointer`;
    }

    // Si ya se alcanzó el límite y el asiento no está seleccionado, deshabilitarlo
    // Solo aplicar límites si la venta NO está abierta y NO es entrada gratuita
    if (!isOpenSale && !isFreeTicket) {
      const isPalco = seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco');
      const seatCount = isPalco ? palcoCapacity : 1;
      
      if (effectiveSeatCount + seatCount > maxTicketsPerCode) {
        const cursorClass = viewMode ? "" : "cursor-not-allowed";
        return `${baseClasses} bg-gray-300 border-gray-400 ${cursorClass} opacity-50`;
      }
    }

    const seatType = seatTypeList.find(t => t.type === seat.type);
    if (seatType && seatType.color) {
      const borderClass = seatType.color.replace('bg-', 'border-').replace('-500', '-700').replace('-600', '-800');
      const hoverClass = seatType.color.replace('-500', '-600').replace('-600', '-700');
      return `${baseClasses} ${seatType.color} ${borderClass} ${hoverClass}`;
    }
    return `${baseClasses} bg-gray-500 border-gray-700 hover:bg-gray-600`;
  };

  const totalPrice = useMemo(() => {
    let total = 0;
    
    // Sumar precio de asientos seleccionados
    selectedSeats.forEach(seatId => {
      const seat = allSeats.find((s) => s.id === seatId);
      const seatPrice = seat?.price || 0;
      const numericPrice = typeof seatPrice === 'number' ? seatPrice : parseFloat(seatPrice) || 0;
      total += numericPrice;
    });
    
    // Si hay reservas existentes, solo considerar las que NO están en selectedSeats
    // para evitar duplicación (las reservas existentes ya están incluidas en selectedSeats)
    if (existingReservations && existingReservations.length > 0) {
      existingReservations.forEach(reservation => {
        const isInSelectedSeats = selectedSeats.includes(reservation.compositeId);
        
        // Solo incluir reservas pendientes que NO estén en selectedSeats y no estén marcadas para cancelación
        if (reservation.status === 'pending' && !isInSelectedSeats && !reservationsToCancel.some(r => r.id === reservation.id)) {
          const reservationPrice = parseFloat(reservation.price) || 0;
          total += reservationPrice;
        }
        
        // Si hay reservas marcadas para devolución, restar su precio
        if (reservationsToReturn && reservationsToReturn.some(r => r.id === reservation.id)) {
          const reservationPrice = parseFloat(reservation.price) || 0;
          total -= reservationPrice;
        }
      });
    }
    
    return total;
  }, [selectedSeats, allSeats, existingReservations, reservationsToCancel, reservationsToReturn]);

  const plantaBajaSeats = allSeats.filter((s) => s.section === "planta-baja");
  const primerPisoSeats = allSeats.filter((s) => s.section === "primer-piso");

  return {
    allSeats,
    selectedSeats,
    setSelectedSeats, 
    totalPrice,
    plantaBajaSeats,
    primerPisoSeats,
    handleSeatClick,
    getSeatClassName,
    seatTypeList,
    seatTypePriceMap,
    maxTicketsPerCode,
    effectiveSeatCount,
    palcoCapacity,
    canSelectMore,
    // Estados de loading y error para tipos de asientos
    seatTypesLoading,
    seatTypesError,
    // Estados para asientos ocupados
    occupiedSeats,
    loadingOccupiedSeats,
    loadOccupiedSeats,
    clearSelectedSeats,
    loadExistingSeats,
    setSeatDeselectedCallback,
    setSeatReselectedCallback,
    createDefaults,
    refetch: refetchSeatTypes,
    validateSeatSelection,
    getRecommendedSeats,
  };
};